import React, { useEffect, useState } from 'react'
import { Camera, Image, Plus, Upload, Video } from 'lucide-react'
import { Button } from 'components/ui'
import { CameraCapture } from 'components/ui/CameraCapture'
import useLocaleText from 'components/useLocaleText'
import styles from './styles.module.css'

export const mediaUploadLiterals = {
  camera: { key: 'attachment_take_photo', fallback: { en: 'Take a photo', he: 'צילום תמונה' } },
  image: { key: 'attachment_upload_photo', fallback: { en: 'Upload photo', he: 'העלאת תמונה' } },
  video: { key: 'task_creation_upload_video', fallback: { en: 'Upload video', he: 'העלאת וידאו' } },
  failed: {
    key: 'task_creation_media_upload_failed',
    fallback: {
      en: 'Upload failed. Retry or remove the file before continuing.',
      he: 'ההעלאה נכשלה. יש לנסות שוב או להסיר את הקובץ לפני ההמשך.',
    },
  },
  file: { key: 'attachment_upload_file', fallback: { en: 'Upload file', he: 'העלאת קובץ' } },
  dropImagesHint: {
    key: 'task_creation_reference_images_drop_hint',
    fallback: { en: 'Drop images here or choose how to add', he: 'גררו תמונות לכאן או בחרו כיצד להוסיף' },
  },
  dropHint: {
    key: 'task_creation_reference_drop_hint',
    fallback: { en: 'Drop files here or choose how to add', he: 'גררו קבצים לכאן או בחרו כיצד להוסיף' },
  },
} as const
export const missingMediaUploadLiteralKeys = [
  'task_creation_upload_video',
  'task_creation_reference_drop_hint',
  'task_creation_reference_images_drop_hint',
  'task_creation_media_upload_failed',
]

interface Props {
  onImage: () => void
  onVideo?: () => void
  onFile?: () => void
  onCapture?: (file: File) => void
  disabled?: boolean
}

export const MediaUploadActions: React.FC<Props> = ({ onImage, onVideo, onFile, onCapture, disabled }) => {
  const t = useLocaleText(mediaUploadLiterals)
  const [cameraOpen, setCameraOpen] = useState(false)

  const actions = [
    { label: t.file, Icon: Upload, onClick: onFile },
    { label: t.camera, Icon: Camera, onClick: onCapture ? () => setCameraOpen(true) : undefined },
    { label: t.image, Icon: Image, onClick: onImage },
    { label: t.video, Icon: Video, onClick: onVideo },
  ]

  useEffect(() => {
    if (disabled || !onCapture) setCameraOpen(false)
  }, [disabled, onCapture])

  return (
    <div className={styles.actions}>
      <p className={styles.hint}>{onVideo || onFile ? t.dropHint : t.dropImagesHint}</p>
      <span className={styles.addHint} aria-hidden="true">
        <Plus size={24} strokeWidth={1.5} />
      </span>
      <div className={styles.buttons}>
        {actions.map(({ label, Icon, onClick }) =>
          onClick ? (
            <Button
              key={label}
              type="button"
              variant="secondary"
              height="32px"
              minWidth="0"
              px="12px"
              fontSize="12px"
              fontWeight={400}
              borderWidth="1px"
              borderColor="#dfe4e8"
              borderRadius="7px"
              color="#22272a"
              isDisabled={disabled}
              startIcon={<Icon size={14} />}
              onClick={onClick}
            >
              {label}
            </Button>
          ) : null
        )}
      </div>
      {cameraOpen && !disabled && onCapture && (
        <CameraCapture onCapture={onCapture} onClose={() => setCameraOpen(false)} />
      )}
    </div>
  )
}
